<template>
  <section class="element-picker-dock" aria-label="Selected elements">
    <p class="flex shrink-0 items-center gap-1.5 pr-1.5 pl-2 text-xs/none font-bold whitespace-nowrap text-heading">
      <ViewfinderCircleIcon class="size-4 text-[var(--element-picker-pink)]" aria-hidden="true" />{{ items.length }}
      selected
    </p>
    <p v-if="!items.length" class="pr-2.5 text-[12.5px]/none whitespace-nowrap text-muted">
      Click anything on the page to add it
    </p>
    <ol v-else class="element-picker-dock__chips">
      <li
        v-for="item in items"
        :key="item.key"
        class="element-picker-chip"
        @mouseenter="emit('hover', item.key)"
        @mouseleave="emit('hover', null)">
        <span class="element-picker-chip__number" aria-hidden="true">{{ item.number }}</span>
        <button
          type="button"
          class="flex max-w-[220px] min-w-0 items-baseline gap-1.5 disabled:cursor-default"
          :title="item.onPage ? 'Scroll to element' : (item.note ?? undefined)"
          :disabled="!item.onPage"
          @click="emit('locate', item.key)">
          <span class="text-[12.5px]/none font-semibold text-heading">{{ item.label }}</span>
          <span v-if="item.text || item.note" class="truncate text-xs/[1.2] text-muted">{{
            item.text || item.note
          }}</span>
        </button>
        <button
          type="button"
          class="element-picker-chip__remove"
          :aria-label="`Remove ${item.label} ${item.number}`"
          title="Remove"
          @click="emit('remove', item.key)">
          <XMarkIcon class="size-3.5" aria-hidden="true" />
        </button>
      </li>
    </ol>
    <Button
      v-if="items.length"
      label="Clear"
      text
      size="small"
      class="element-picker-dock__clear"
      @click="emit('clear')" />
  </section>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import { ViewfinderCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import type { ElementPickerItem } from './elementPickerItem'

defineProps<{ items: ElementPickerItem[] }>()
const emit = defineEmits<{ hover: [key: string | null]; remove: [key: string]; locate: [key: string]; clear: [] }>()
</script>

<style scoped>
.element-picker-dock {
  position: absolute;
  bottom: 16px;
  left: 50%;
  z-index: 4;
  display: flex;
  max-width: calc(100% - 32px);
  align-items: center;
  gap: 6px;
  padding: 6px;
  border: 1px solid var(--rm-line);
  border-radius: 12px;
  background: var(--rm-app-card);
  box-shadow: var(--shadow-popover);
  transform: translateX(-50%);
}
.element-picker-dock__chips {
  display: flex;
  min-width: 0;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}
.element-picker-chip {
  display: flex;
  height: 32px;
  flex: none;
  align-items: center;
  gap: 7px;
  padding: 0 4px 0 6px;
  border: 1px solid var(--rm-line);
  border-radius: 8px;
  transition: background-color 120ms;
}
.element-picker-chip:hover {
  border-color: transparent;
  background: var(--element-picker-tint);
}
.element-picker-chip__number {
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 4px;
  background: var(--element-picker-pink);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.element-picker-chip__remove {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  color: var(--rm-muted);
}
.element-picker-chip__remove:hover {
  background: var(--rm-danger-soft);
  color: var(--rm-danger);
}
.element-picker-dock__clear.p-button {
  padding: 7px 10px;
  font-size: 12.5px;
}
</style>
